export type MetricBlockProps = {
  label: string;
  value: string | number;
  unit?: string;
  band?: { low: number; high: number; ci: number; unit?: string };
  why?: string;
  state?: 'default' | 'loading' | 'stale' | 'permission';
  tone?: 'neutral' | 'critical';
};

/**
 * Single high-importance scalar or ML output with optional uncertainty
 * band + one-line explainer. ADR-driven invariant: ML outputs MUST carry
 * a band; the `band` prop is therefore optional only because scalars
 * (e.g. RMS) don't have one.
 */
export function MetricBlock({
  label,
  value,
  unit,
  band,
  why,
  state = 'default',
  tone = 'neutral',
}: MetricBlockProps) {
  if (state === 'loading') {
    return (
      <div className="animate-pulse rounded-r-card border border-gray-300 bg-gray-50 p-sp-4">
        <div className="h-3 w-24 rounded-r-chip bg-gray-100" />
        <div className="mt-sp-3 h-7 w-28 rounded-r-chip bg-gray-100" />
        <div className="mt-sp-2 h-3 w-40 rounded-r-chip bg-gray-100" />
      </div>
    );
  }

  if (state === 'permission') {
    return (
      <div className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-4 text-gray-700">
        <div className="text-fs-eyebrow font-medium uppercase tracking-[0.14em]">
          {label}
        </div>
        <div className="mt-sp-2 font-mono text-fs-statement">Restricted</div>
        <div className="mt-sp-1 text-fs-meta">Permission required.</div>
      </div>
    );
  }

  const valueClass =
    tone === 'critical'
      ? 'font-mono text-fs-h1 font-medium text-critical'
      : 'font-mono text-fs-h1 font-medium text-gray-900';

  return (
    <div
      className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-4"
      data-state={state}
      data-tone={tone}
    >
      {/* dl is dt/dd only — band/why move outside so axe's
          definition-list rule passes. */}
      <dl>
        <dt className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
          {label}
        </dt>
        <dd className="mt-sp-2 flex items-baseline gap-sp-2">
          <span className={valueClass}>{value}</span>
          {unit ? (
            <span className="font-mono text-fs-meta text-gray-700">{unit}</span>
          ) : null}
          {state === 'stale' ? (
            <span className="text-fs-meta text-warn-fg">· stale</span>
          ) : null}
        </dd>
      </dl>
      {band ? (
        <output className="mt-sp-2 block font-mono text-fs-meta text-gray-700">
          {band.low.toFixed(1)}–{band.high.toFixed(1)}
          {band.unit ? ` ${band.unit}` : ''} (CI {Math.round(band.ci * 100)}%)
        </output>
      ) : null}
      {why ? (
        <p className="mt-sp-2 text-fs-meta text-gray-700">{why}</p>
      ) : null}
    </div>
  );
}
